import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import {
  Shield, Smartphone, Key, RefreshCw, Copy, Check, AlertTriangle,
  Loader2, Eye, EyeOff, Lock, Unlock, QrCode, Download
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

export default function MFASettings() {
  const [mfaStatus, setMfaStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Setup flow states
  const [setupModalOpen, setSetupModalOpen] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  
  // Disable flow states
  const [disableModalOpen, setDisableModalOpen] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [disabling, setDisabling] = useState(false);
  const [showDisablePassword, setShowDisablePassword] = useState(false);
  
  // Regenerate backup codes states
  const [regenerateModalOpen, setRegenerateModalOpen] = useState(false);
  const [regeneratePassword, setRegeneratePassword] = useState('');
  const [regenerateCode, setRegenerateCode] = useState('');
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    fetchMFAStatus();
  }, []);

  const fetchMFAStatus = async () => {
    try {
      const response = await axios.get(`${API}/mfa/status`);
      setMfaStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch MFA status:', error);
      toast.error('Failed to load MFA status');
    } finally {
      setLoading(false);
    }
  };

  const handleStartSetup = async () => {
    try {
      const response = await axios.post(`${API}/mfa/setup`);
      setSetupData(response.data);
      setSetupModalOpen(true);
    } catch (error) {
      console.error('Failed to start MFA setup:', error);
      toast.error(error.response?.data?.detail || 'Failed to start MFA setup');
    }
  };

  const handleVerifyAndEnable = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    setVerifying(true);
    try {
      const response = await axios.post(`${API}/mfa/verify`, {
        code: verificationCode
      });
      
      setBackupCodes(response.data.backup_codes);
      setShowBackupCodes(true);
      toast.success('MFA enabled successfully!');
      fetchMFAStatus();
    } catch (error) {
      console.error('Failed to verify MFA:', error);
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setVerifying(false);
    }
  };

  const handleDisableMFA = async () => {
    if (!disablePassword || !disableCode) {
      toast.error('Please enter your password and verification code');
      return;
    }

    setDisabling(true);
    try {
      await axios.post(`${API}/mfa/disable`, {
        password: disablePassword,
        code: disableCode
      });
      
      toast.success('MFA has been disabled');
      setDisableModalOpen(false);
      setDisablePassword('');
      setDisableCode('');
      fetchMFAStatus();
    } catch (error) {
      console.error('Failed to disable MFA:', error);
      toast.error(error.response?.data?.detail || 'Failed to disable MFA');
    } finally {
      setDisabling(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    if (!regeneratePassword || !regenerateCode) {
      toast.error('Please enter your password and verification code');
      return;
    }

    setRegenerating(true);
    try {
      const response = await axios.post(`${API}/mfa/regenerate-backup-codes`, {
        password: regeneratePassword,
        code: regenerateCode
      });
      
      setBackupCodes(response.data.backup_codes);
      setShowBackupCodes(true);
      setRegenerateModalOpen(false);
      setRegeneratePassword('');
      setRegenerateCode('');
      toast.success('Backup codes regenerated successfully!');
      fetchMFAStatus();
    } catch (error) {
      console.error('Failed to regenerate backup codes:', error);
      toast.error(error.response?.data?.detail || 'Failed to regenerate backup codes');
    } finally {
      setRegenerating(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const downloadBackupCodes = () => {
    if (!backupCodes) return;
    
    const content = `CyberForte MFA Backup Codes
Generated: ${new Date().toLocaleString()}

Store these codes in a safe place. Each code can only be used once.

${backupCodes.map((code, i) => `${i + 1}. ${code}`).join('\n')}

IMPORTANT: These codes will not be shown again!`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'cyberforte-mfa-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Backup codes downloaded!');
  };

  const closeSetupModal = () => {
    setSetupModalOpen(false);
    setSetupData(null);
    setVerificationCode('');
    setShowBackupCodes(false);
    setBackupCodes(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mfa-settings">
      {/* MFA Status Card */}
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${mfaStatus?.mfa_enabled ? 'bg-green-100' : 'bg-slate-100'}`}>
                <Shield className={`w-5 h-5 ${mfaStatus?.mfa_enabled ? 'text-green-600' : 'text-slate-500'}`} />
              </div>
              <div>
                <CardTitle className="text-lg text-slate-900">Two-Factor Authentication</CardTitle>
                <CardDescription className="text-slate-600">
                  Add an extra layer of security to your account
                </CardDescription>
              </div>
            </div>
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${
              mfaStatus?.mfa_enabled 
                ? 'bg-green-100 text-green-700' 
                : 'bg-slate-100 text-slate-600'
            }`} data-testid="mfa-status-badge">
              {mfaStatus?.mfa_enabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
        </CardHeader>
        <CardContent>
          {mfaStatus?.mfa_enabled ? (
            <div className="space-y-4">
              {/* MFA Info */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Enabled on:</span>
                  <span className="text-slate-900 font-medium">
                    {mfaStatus.mfa_enabled_at ? new Date(mfaStatus.mfa_enabled_at).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Last used:</span>
                  <span className="text-slate-900 font-medium">
                    {mfaStatus.mfa_last_used_at ? new Date(mfaStatus.mfa_last_used_at).toLocaleDateString() : 'Never'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">Backup codes remaining:</span>
                  <span className={`font-medium ${
                    mfaStatus.backup_codes_remaining <= 2 ? 'text-red-600' : 'text-slate-900'
                  }`}>
                    {mfaStatus.backup_codes_remaining}
                  </span>
                </div>
              </div>

              {/* Warning for low backup codes */}
              {mfaStatus.backup_codes_remaining <= 2 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Low backup codes</p>
                    <p className="text-sm text-amber-700">
                      You have only {mfaStatus.backup_codes_remaining} backup codes remaining. 
                      Consider regenerating your backup codes.
                    </p>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  onClick={() => setRegenerateModalOpen(true)}
                  className="border-slate-300 text-slate-700"
                  data-testid="regenerate-backup-codes-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Regenerate Backup Codes
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setDisableModalOpen(true)}
                  className="border-red-200 text-red-600 hover:bg-red-50"
                  data-testid="disable-mfa-btn"
                >
                  <Unlock className="w-4 h-4 mr-2" />
                  Disable MFA
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Benefits */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-medium text-slate-900 mb-3">Why enable MFA?</h4>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    Protects your account even if your password is compromised
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    Compatible with Google Authenticator, Microsoft Authenticator, Authy
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    Backup codes available for emergency access
                  </li>
                </ul>
              </div>

              <Button
                onClick={handleStartSetup}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="enable-mfa-btn"
              >
                <Shield className="w-4 h-4 mr-2" />
                Enable Two-Factor Authentication
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* MFA Setup Modal */}
      <Dialog open={setupModalOpen} onOpenChange={closeSetupModal}>
        <DialogContent className="bg-white border-slate-200 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              {showBackupCodes ? (
                <>
                  <Key className="w-5 h-5" />
                  Save Your Backup Codes
                </>
              ) : (
                <>
                  <Smartphone className="w-5 h-5" />
                  Set Up Two-Factor Authentication
                </>
              )}
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              {showBackupCodes
                ? "Store these codes in a safe place. Each code can only be used once."
                : "Scan the QR code with your authenticator app, then enter the verification code."
              }
            </DialogDescription>
          </DialogHeader>

          {showBackupCodes ? (
            <div className="space-y-4 py-4">
              {/* Backup Codes */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-2">
                  {backupCodes?.map((code, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-white rounded px-3 py-2 font-mono text-sm"
                    >
                      <span>{code}</span>
                      <button
                        onClick={() => copyToClipboard(code)}
                        className="text-slate-400 hover:text-slate-600"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Warning */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800">Important!</p>
                  <p className="text-amber-700">
                    These codes will NOT be shown again. Save them somewhere safe like a password manager.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={downloadBackupCodes}
                  variant="outline"
                  className="flex-1 border-slate-300 text-slate-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Codes
                </Button>
                <Button
                  onClick={() => copyToClipboard(backupCodes.join('\n'))}
                  variant="outline"
                  className="flex-1 border-slate-300 text-slate-700"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy All
                </Button>
              </div>

              <Button
                onClick={closeSetupModal}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white"
              >
                I've Saved My Codes
              </Button>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              {/* QR Code */}
              {setupData?.qr_code && (
                <div className="flex justify-center">
                  <div className="bg-white p-4 rounded-lg border border-slate-200">
                    <img
                      src={setupData.qr_code}
                      alt="MFA QR Code"
                      className="w-48 h-48"
                      data-testid="mfa-qr-code"
                    />
                  </div>
                </div>
              )}

              {/* Manual Entry Key */}
              {setupData?.secret && (
                <div className="bg-slate-50 rounded-lg p-4">
                  <Label className="text-slate-700 text-sm">Can't scan? Enter this key manually:</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <code className="flex-1 bg-white px-3 py-2 rounded border border-slate-200 font-mono text-sm break-all">
                      {setupData.secret}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(setupData.secret)}
                      className="text-slate-500"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Verification Code Input */}
              <div>
                <Label htmlFor="mfa-code" className="text-slate-700">Verification Code</Label>
                <Input
                  id="mfa-code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  className="mt-1 bg-white border-slate-300 text-slate-900 text-center text-lg tracking-widest font-mono"
                  maxLength={6}
                  data-testid="mfa-verification-input"
                />
              </div>

              <Button
                onClick={handleVerifyAndEnable}
                disabled={verifying || verificationCode.length !== 6}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="verify-mfa-btn"
              >
                {verifying ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Verify & Enable MFA
                  </>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Disable MFA Modal */}
      <Dialog open={disableModalOpen} onOpenChange={setDisableModalOpen}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Unlock className="w-5 h-5 text-red-600" />
              Disable Two-Factor Authentication
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              This will remove the extra layer of security from your account.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-red-800">Warning</p>
                <p className="text-red-700">
                  Disabling MFA will make your account less secure. Only proceed if absolutely necessary.
                </p>
              </div>
            </div>

            <div>
              <Label htmlFor="disable-password" className="text-slate-700">Current Password</Label>
              <div className="relative mt-1">
                <Input
                  id="disable-password"
                  type={showDisablePassword ? "text" : "password"}
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  placeholder="Enter your password"
                  className="bg-white border-slate-300 text-slate-900 pr-10"
                  data-testid="disable-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowDisablePassword(!showDisablePassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showDisablePassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="disable-code" className="text-slate-700">Authenticator Code</Label>
              <Input
                id="disable-code"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit code"
                className="mt-1 bg-white border-slate-300 text-slate-900 text-center tracking-widest font-mono"
                maxLength={6}
                data-testid="disable-code-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDisableModalOpen(false)}
              className="border-slate-300 text-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDisableMFA}
              disabled={disabling || !disablePassword || disableCode.length !== 6}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-disable-mfa-btn"
            >
              {disabling ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Disabling...
                </>
              ) : (
                'Disable MFA'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Regenerate Backup Codes Modal */}
      <Dialog open={regenerateModalOpen} onOpenChange={setRegenerateModalOpen}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <RefreshCw className="w-5 h-5" />
              Regenerate Backup Codes
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              This will invalidate all existing backup codes and generate new ones.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="regen-password" className="text-slate-700">Current Password</Label>
              <Input
                id="regen-password"
                type="password"
                value={regeneratePassword}
                onChange={(e) => setRegeneratePassword(e.target.value)}
                placeholder="Enter your password"
                className="mt-1 bg-white border-slate-300 text-slate-900"
                data-testid="regen-password-input"
              />
            </div>

            <div>
              <Label htmlFor="regen-code" className="text-slate-700">Authenticator Code</Label>
              <Input
                id="regen-code"
                value={regenerateCode}
                onChange={(e) => setRegenerateCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter 6-digit code"
                className="mt-1 bg-white border-slate-300 text-slate-900 text-center tracking-widest font-mono"
                maxLength={6}
                data-testid="regen-code-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRegenerateModalOpen(false)}
              className="border-slate-300 text-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRegenerateBackupCodes}
              disabled={regenerating || !regeneratePassword || regenerateCode.length !== 6}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="confirm-regen-btn"
            >
              {regenerating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Regenerating...
                </>
              ) : (
                'Regenerate Codes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
